import { getAllLocations } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { LoaderCircle, TrashIcon } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { AddTemplateForm } from '@/features/site-templates/add-template';

// Ports app/[brand]/admin/templates/add/page.tsx: create a site-template from an
// existing site. Super-admin only (non-super-admins are sent to the list, as the
// section was only reachable from the super-admin nav). Loads the sites for the
// source-site select.
export function TemplateAdd() {
  const t = useTranslations('admin');
  const { slug, loading } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const { data: locations, isLoading } = useSWR('admin/all-locations', () =>
    getAllLocations(),
  );

  if (loading || roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!access?.superAdmin) {
    return <Navigate to={`/${slug}/admin/templates`} replace />;
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('templates')}</PageTitle>
        <PageButtons>
          <Button variant="destructive" asChild>
            <Link to={`/${slug}/admin/templates`}>
              <TrashIcon className="size-4" />
              {t('discard')}
            </Link>
          </Button>
        </PageButtons>
      </PageHeader>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('add', { what: t('template').toLowerCase() })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoaderCircle className="text-primary size-8 animate-spin" />
            </div>
          ) : (
            <AddTemplateForm locations={locations ?? []} />
          )}
        </CardContent>
      </Card>
    </PageBox>
  );
}
